import type { Container } from "pixi.js";
import type { ReelMotionTuning } from "./ReelMotionTuning";
import { ReelColumn } from "./ReelColumn";
import { BUFFER_ROWS_ABOVE, computeSpinParams } from "./ReelSpinParams";
import type { ReelColumnContext, ReelLandStaggerCursor, ReelLane } from "./ReelSpinParams";
import type { ComputedGridLayout, GridLayout, GridSymbolPool, PresentationTaskRunner, SpeedMode, SymbolDefinition, SymbolView } from "@slot/presentation";

/**
 * The rolling-drum spin and its staggered stop (internal: not on the package entry).
 *
 * ORCHESTRATOR: builds the drums, computes the immutable per-spin `SpinParams`, owns the
 * continuous/abort/overlay/runId/slam-flag state + the public API, and runs the single
 * thin per-frame DRIVER. All per-column motion — the HOLD→…→DONE phase machine, the
 * reveal-then-brake natural stop, the synchronized slam, the analytic offset + lane keying
 * — lives in `ReelColumn` (with `SpinParams` + `ReelColumnContext` + ease helpers).
 *
 * Each reel (column) owns a vertical "drum" of pooled SymbolViews: gridRowCount
 * visible lanes plus a small buffer above, tiling a band exactly drumHeight tall
 * (the surplus is clipped by GridContainerMask). The drum scrolls down at
 * constant velocity; lanes that fall past the bottom wrap to the top via modulo
 * and are re-symboled, giving an endless reel from a fixed, small pool footprint.
 *
 * ── Scope: "reel_motion" ───────────────────────────────────────────────────
 * The drum runs under its OWN task-runner scope, which the reels plugin does not
 * declare as a slam scope: the shell neither arms it in turbo nor force-finishes it
 * on a slam. That makes the drum self-determining: it reads the speed mode itself
 * (lockstep + shorter spin in turbo/super_turbo) and runs its OWN animated stop on
 * a manual slam, instead of being instant-cancelled. The shell delivers the
 * manual-slam signal via `GamePresentation.slam` → requestSlamStop().
 *
 * ── The thin per-frame driver (spinAndForget) ───────────────────────────────
 * One driver advances ALL reels every frame via `runner.tickInScope`: on a slam it captures
 * ONE shared end-time + calls `column.applySlam` on every not-DONE reel; then
 * `column.tick(now, landCtx)` on every column (landCtx = the shared left→right land-stagger
 * cursor); then it carries the attached column overlays. `now` is the tick's logical time
 * (engine seconds at the clock's speed-mode rate), NOT wall-clock, so it pauses with the
 * engine and runs faster in turbo / super turbo with no explicit scaling.
 *
 * The reveal-then-brake natural stop, the synchronized slam, the overshoot/settle, and
 * the filler/finals lane keying are all per-column — see `ReelColumn`.
 */

export class ReelDrum {
  /**
   * The drum's OWN runner scope. The reels plugin declares no slam scope (`slamScopes: { presentation: [] }`),
   * so the shell never arms or force-finishes it; the slam reaches the drum through `requestSlamStop`.
   */
  public static readonly SCOPE = "reel_motion";

  private readonly pool: GridSymbolPool;
  private readonly layoutUtil: GridLayout;
  /**
   * Optional per-symbol pre-blurred FILLER definitions (symbolId → a definition whose
   * sprite frame is that symbol's pre-blurred variant). When set, every FILLER lane
   * (random scroll) displays the pre-blurred frame instead of the crisp one — motion
   * blur baked into the art (one texture sample, fully batched). Finals/landed stay
   * crisp. Null (not wired) → the drum spins crisp.
   */
  private readonly fillerBlurDefs: Map<string, SymbolDefinition> | null;

  private columns: ReelColumn[] = [];
  private candidateSymbolIds: readonly string[] = [];

  // On-screen views ADOPTED as drum lanes by the current buildDrum (see it for why). The grid
  // must NOT release these when it drops the static grid — the drum owns them until their
  // lane recycles. Rebuilt per buildDrum; queried via wasAdoptedAsLane.
  private readonly adoptedViews: Set<SymbolView> = new Set();

  // External overlays handed to the running drum to scroll out WITH a column (e.g. a
  // dropping expanding-wild at spin-start). Each is driven every tick by its column's
  // scroll offset (so it scrolls in lockstep with the reel symbols) and resolved — the
  // owner then destroys the node — once it clears the window (or the spin tears down).
  private columnOverlays: { node: Container; col: number; startY: number; resolve: () => void }[] = [];

  private slamRequested = false;
  private isSpinning = false;

  // ── Continuous spin (spin-on-click, stop-on-data) state ────────────────────
  // spinAndForget scrolls indefinitely until the server response provides the
  // final grid, then brakes onto it. These fields carry the late signal.
  private continuousActive = false;
  private continuousFinals: string[][] | null = null;
  private continuousAnticipation: number[] | null = null; // server-flagged anticipation columns
  private continuousAbort = false;

  // Generation token: bumped at every spinAndForget start. A driver only runs its
  // teardown (resetting the shared flags) if its run is still current — so a fast
  // re-spin's freshly-set flags can't be clobbered by a stale driver's finally.
  private runId = 0;

  public constructor(input: {
    pool: GridSymbolPool;
    layoutUtil: GridLayout;
    fillerBlurDefs?: Map<string, SymbolDefinition> | null;
  }) {
    this.pool = input.pool;
    this.layoutUtil = input.layoutUtil;
    this.fillerBlurDefs = input.fillerBlurDefs ?? null;
  }

  /**
   * Bind a symbol to a drum lane — the single place the "filler blurs, finals stay
   * crisp" rule lives. A FILLER symbol (random scroll, or an off-window buffer lane)
   * gets its pre-blurred variant frame (motion blur baked into the art); a real result
   * symbol stays crisp. `applyDefinition` re-fits the blurred frame to the cell (the game
   * tunes its per-symbol `blurredScale`/offset so the displayed size matches the crisp);
   * the lane's logical `symbolId` (set by `applySymbolIdToView`) is unchanged either way,
   * so finals/landing logic reads the crisp id. No blur def wired → it stays crisp (opt-in).
   */
  private applyLaneSymbol(view: SymbolView, symbolId: string, isFiller: boolean): void {
    // FILLER lanes are transient — random scroll fodder, plain blurred sprites, no per-lane
    // skeleton. A FINAL is the cell's real result: mark it `pendingLand` so a hook can show it in
    // its pre-land pose from the moment it arrives, rather than the cell flipping from sprite to
    // spine when it lands.
    this.pool.applySymbolIdToView(view, symbolId, { transient: isFiller, pendingLand: !isFiller });
    if (!isFiller) return;
    const blurred = this.fillerBlurDefs?.get(symbolId);
    if (blurred) view.applyDefinition(blurred);
  }

  /**
   * Whether this view was ADOPTED from the on-screen grid as a drum lane by the current
   * `buildDrum`. The grid checks this before releasing the static grid: an adopted view is owned
   * by the drum until its lane recycles, so releasing it there would pull it out from under
   * the running drum (double release + a lane pointing at a pooled view).
   */
  public wasAdoptedAsLane(view: SymbolView): boolean {
    return this.adoptedViews.has(view);
  }

  /**
   * The player's slam (the shell's `GamePresentation.slam`). Flags the driver, which brakes every
   * still-spinning column together on its first frame with the finals known — a slam before the
   * server's answer is held until it arrives (decision 20). A slam before the scroll starts is not
   * taken — there is nothing to brake — which is why hold-to-spin's auto-slam waits for the answer
   * (`HoldToSpinPolicy`): the coordinator announces "requesting" before its pre-spin starts the drum.
   *
   * True only when this call took the slam, so the shell plays the slam cue and haptic once per spin
   * and never for a tap with nothing to stop. Until 2026-09-26 it answered true on every call once a
   * round had run (the 2026-09-11 review's finding 7).
   */
  public requestSlamStop(): boolean {
    if (!this.isSpinning || this.slamRequested) return false;
    this.slamRequested = true;
    return true;
  }

  /** True while a spin-and-forget continuous scroll is running. */
  public isContinuousActive(): boolean {
    return this.continuousActive;
  }

  /**
   * Server response arrived: hand the final grid to the running continuous
   * scroll so it brakes onto it (honoring min-spin). string[col][row]. No-op if
   * no continuous scroll is active.
   */
  public provideContinuousFinals(finals: string[][], anticipatedColumns?: number[]): void {
    this.continuousFinals = finals;
    this.continuousAnticipation = anticipatedColumns ?? null;
  }

  /**
   * Abort a running continuous scroll WITHOUT braking (error / rejected spin /
   * recovery — no final grid will arrive). Sets continuousAbort (the scroll
   * loop's applyOff is guarded on it, so per-leg view writes stop immediately)
   * AND synchronously clears continuousActive — so a NEXT spin's
   * startContinuousSpin sees no active scroll right away, even though the
   * awaited loop only fully unwinds (and runs its finally) on its next poll.
   * The loop's finally is idempotent with this. Idempotent overall.
   */
  public abortContinuous(): void {
    if (!this.continuousActive) return;
    this.continuousAbort = true;
    this.continuousActive = false;
  }

  /**
   * Hand an external overlay node to column `col` so the RUNNING drum carries it out:
   * every tick the node's local Y is driven by `col`'s scroll offset, so it scrolls in
   * lockstep with that reel's symbols. It moves from where it is when handed over: the offset the
   * column already scrolled is taken off its start, so a hand-over after the spin began does not
   * jump it down by that much. The returned Promise resolves once the overlay
   * has scrolled a full band below the window (clipped out) OR the spin tears down
   * (slam/abort/land) — whichever first; the caller destroys the node on resolve. The
   * node must already be reparented into the (clipped) grid-root space — this only
   * drives its Y. Resolves immediately if no scroll is active or the node is gone.
   */
  public attachColumnOverlay(col: number, node: Container): Promise<void> {
    if (!this.continuousActive || !node || node.destroyed) return Promise.resolve();
    const startY = node.position.y - (this.columns[col]?.off ?? 0);
    return new Promise<void>((resolve) => {
      this.columnOverlays.push({ node, col, startY, resolve });
    });
  }

  /** Resolve + drop any pending column overlays (teardown). Idempotent. */
  private resolveColumnOverlays(): void {
    for (const ov of this.columnOverlays) ov.resolve();
    this.columnOverlays = [];
  }

  /**
   * Acquire + position the drum so it fully covers the visible window at rest.
   * Call this BEFORE releasing the static grid so the takeover is seamless.
   *
   * When `visibleViewsByColumn` is supplied, each VISIBLE lane ADOPTS its on-screen view
   * AS-IS, so the spin starts from exactly what is displayed and whatever that cell is
   * currently showing scrolls out with it — a held win-overlay (markPoolUnsafe, kept with its
   * overlay child + faded base) and equally a cell running an idle-Spine overlay. Rebuilding
   * the lane from the pool instead would tear those down and pop back to the static sprite the
   * instant a spin starts. Adoption deliberately does NOT call `applyLaneSymbol`, so no
   * transient rebind fires and any attached overlay survives; the recycle path (`rebindLane`)
   * performs that bind once the lane leaves the window, which is what tears the overlay down.
   *
   * The grid must skip adopted views when it releases the static grid (`wasAdoptedAsLane`) —
   * releasing one would pull it out from under the running drum. Lanes with no on-screen view
   * (buffer lanes above the window, or an empty cell) are random fillers as before.
   */
  public buildDrum(input: {
    layout: ComputedGridLayout;
    gridColumnCount: number;
    gridRowCount: number;
    /** The random strip the filler lanes cycle: never empty (the mechanic pads the on-screen ids with the title's fillers). */
    candidateSymbolIds: readonly string[];
    visibleViewsByColumn?: (SymbolView | null)[][];
  }): void {
    this.candidateSymbolIds = input.candidateSymbolIds;

    this.columns = [];
    this.adoptedViews.clear();

    const spacing = input.layout.cellHeight;
    const laneCount = input.gridRowCount + BUFFER_ROWS_ABOVE;

    for (let col = 0; col < input.gridColumnCount; col++) {
      const top = this.layoutUtil.computeLocalPosition(input.layout, 0, col);
      const lanes: ReelLane[] = [];
      for (let k = 0; k < laneCount; k++) {
        const visibleRow = k - BUFFER_ROWS_ABOVE;
        const onScreen =
          visibleRow >= 0 ? input.visibleViewsByColumn?.[col]?.[visibleRow] ?? null : null;

        let view: SymbolView;
        if (onScreen) {
          // ADOPT the on-screen view AS-IS (see the method doc): it already shows this cell
          // exactly — including any live overlay (held win frame, idle Spine) — so the spin
          // takes over with no pop and the overlay keeps playing as it scrolls away. No
          // applyLaneSymbol: that transient bind is what would tear the overlay down, and it
          // belongs on the recycle path once the lane has left the window.
          view = onScreen;
          this.adoptedViews.add(view);
        } else {
          // No on-screen cell backs this lane (a buffer lane above the window, or an empty
          // cell) → a random filler, blurred like every other filler.
          view = this.pool.acquire();
          this.applyLaneSymbol(view, this.pickSymbolId(), true);
        }

        const baseY = top.y - (BUFFER_ROWS_ABOVE - k) * spacing; // buffer lanes ABOVE (Pixi y-down)
        view.node.position.set(top.x, baseY);
        view.node.visible = true;

        lanes.push({ view, baseY, prevY: baseY });
      }
      this.columns.push(new ReelColumn(col, top.x, lanes));
    }
  }

  /**
   * Spin-on-click, stop-on-data. Drives every reel from ONE per-frame loop over
   * shared per-column state (see the class header) — the drum (built by buildDrum)
   * scrolls INDEFINITELY with random/visible symbols until the server response
   * calls provideContinuousFinals(), then each reel brakes onto those finals.
   * Returns a promise that resolves only when every reel has landed AND settled
   * (or the scroll was aborted) — spinToModel awaits it before rendering.
   *
   * Timing contract:
   *  - Each reel scrolls at least `baseMinSpinSeconds` (per mode) before its
   *    natural stop, so a fast/instant response still reads as a real spin.
   *  - Natural stop is staggered left→right (anchored to data arrival); turbo/
   *    super_turbo run lockstep. The clock scales every duration (faster) — and
   *    because `now` is the runner's logical time, that scaling is automatic with
   *    no explicit math here.
   *  - A manual slam (slamRequested) lands EVERY reel together on one shared
   *    end-time, on the finals. A slam that comes before them is held until they
   *    arrive: the reels only ever stop on the result (user, 2026-09-26). Until
   *    then it landed on a random boundary, and the result started a second scroll
   *    over a first drum nobody released (review finding 49).
   *  - abortContinuous() exits with no brake (error/recovery owns the grid).
   */
  public async spinAndForget(input: {
    runner: PresentationTaskRunner;
    layout: ComputedGridLayout;
    gridColumnCount: number;
    gridRowCount: number;
    tuning: ReelMotionTuning;
    mode: SpeedMode;
    /**
     * Fired once per reel the frame it lands — its impact: the overshoot's low point when the
     * stop bounces, before the settle — in left→right stop order. `ReelSpinMechanic` wires it to
     * the grid's land reactions (the `column_drop` thud among them), the land animation, the
     * anticipation and the spin roll's stop.
     */
    onColumnLanded?: (column: number) => void;
    /**
     * Fired once per server-flagged reel the frame it ENTERS the anticipation beat.
     * `ReelSpinMechanic` wires it to the anticipation (the frame over the reel, the zoom, the
     * anticipation roll); without it the beat is motion only.
     */
    onColumnAnticipateStart?: (column: number) => void;
  }): Promise<void> {
    const { runner, layout, gridRowCount, tuning, mode } = input;

    const scope = ReelDrum.SCOPE;
    const params = computeSpinParams(layout, gridRowCount, tuning, mode);

    const ctx: ReelColumnContext = {
      params,
      applyLaneSymbol: (view, symbolId, isFiller) =>
        this.applyLaneSymbol(view, symbolId, isFiller),
      pickSymbolId: () => this.pickSymbolId(),
      acquire: () => this.pool.acquire(),
      release: (view) => this.pool.release(view, "reel_dirty_swap"),
      finals: () => this.continuousFinals,
      aborted: () => this.continuousAbort,
      anticipates: () => this.continuousAnticipation,
      onColumnLanded: input.onColumnLanded,
      onColumnAnticipateStart: input.onColumnAnticipateStart,
    };
    for (const column of this.columns) column.beginSpin(ctx);

    // ── The single per-frame tick: advances EVERY column from shared state. ──
    let slamEndTime = -1; // shared SLAM land time, captured once
    // Land-stagger (natural stop): reels commit strictly in order (nextNaturalCol)
    // and each lands ≥ stopStagger after the previous one (naturalLandCursor). This
    // staggers the LANDINGS, not the starts — so a velocity-matched brake (variable
    // length) can never let a later reel finish before an earlier one. ONE cursor per
    // spin, shared across columns + ticks (each column.tick reads + mutates it).
    const landCtx: ReelLandStaggerCursor = {
      nextNaturalCol: 0,
      naturalLandCursor: Number.NEGATIVE_INFINITY,
      lastLandedCol: -1,
      lastLandedTime: Number.NEGATIVE_INFINITY,
      anticipationStarted: false,
    };
    const tick = (now: number): void => {
      if (this.continuousAbort) return;

      // SLAM: capture ONE shared end-time, retarget every not-DONE column to it so they
      // land on the SAME frame; each column.applySlam runs its own FINISH-vs-commit
      // decision off that shared end-time (HOLD/WINDUP scroll the min-travel floor first;
      // a reel mid natural-decel re-aims forward to the shared end, never reversing).
      if (this.slamRequested && slamEndTime < 0 && this.continuousFinals !== null) {
        slamEndTime = now + params.slamDecelSeconds;
        for (const column of this.columns) {
          if (!column.isDone) column.applySlam(now, slamEndTime);
        }
      }

      // Advance every column one frame: the full phase cascade (HOLD -> ... -> DONE),
      // the SCROLL land-stagger commit-gate, the brake-start, and the landings all live
      // in ReelColumn.tick now; landCtx sequences the left-to-right stagger ACROSS columns.
      for (const column of this.columns) column.tick(now, landCtx);

      // Carry any attached column overlays (e.g. a dropping expanding-wild) out WITH
      // the reel: drive each by ITS column's current scroll offset so it scrolls in
      // lockstep, and resolve (→ the owner destroys it) once it's a full band below
      // the window (clipped out by the grid mask). Back-to-front for safe splice.
      for (let i = this.columnOverlays.length - 1; i >= 0; i--) {
        const ov = this.columnOverlays[i];
        if (ov.node.destroyed) {
          ov.resolve();
          this.columnOverlays.splice(i, 1);
          continue;
        }
        const off = this.columns[ov.col]?.off ?? 0;
        ov.node.position.set(ov.node.position.x, ov.startY + off);
        if (off >= params.drumHeight) {
          ov.resolve();
          this.columnOverlays.splice(i, 1);
        }
      }
    };

    // ── Driver: ONE per-frame tick (runner.tickInScope) inside a SINGLE runInScope. `now` is
    // LOGICAL seconds from 0 (engine time at the clock's speed-mode rate, NOT wall clock), so it
    // pauses cleanly and runs faster in turbo with zero explicit scaling; all motion is an analytic
    // function of it. The reel's OWN slam is handled in tick(); isDone stops on a superseded run
    // (runId) or abort, else when all reels land.
    this.continuousFinals = null;
    this.continuousAnticipation = null;
    this.continuousAbort = false;
    this.continuousActive = true;
    this.isSpinning = true;
    const myRun = ++this.runId;
    const allDone = (): boolean => this.columns.every((c) => c.isDone);
    try {
      await runner.runInScope(scope, async () => {
        await runner.tickInScope(
          scope,
          (now) => tick(now),
          () => this.runId !== myRun || this.continuousAbort || allDone(),
        );
      });
    } finally {
      // Generation-guarded: a fast re-spin bumps runId, so a STALE driver's
      // teardown can't clobber the new run's flags.
      if (this.runId === myRun) {
        this.continuousActive = false;
        this.isSpinning = false;
        this.slamRequested = false;
        this.continuousFinals = null;
        this.continuousAnticipation = null;
        this.continuousAbort = false;
        this.resolveColumnOverlays();
      }
    }
  }

  /**
   * Release every drum view back to the pool. Idempotent — the columns are emptied, so a
   * second call releases nothing: `ReelSpinMechanic.resetPresentationState` releases the drum,
   * then again through `clearAllTransitions`, which every render also runs.
   */
  public releaseDrum(skip?: (view: SymbolView) => boolean): void {
    for (const column of this.columns) {
      for (const lane of column.lanes) {
        // Leave the views the caller's grid owns: `clearAllTransitions` skips the landed views
        // the grid adopted as its cells (`ReelLandAnimations.owns`), still landing or not —
        // releasing one would recycle a live cell out from under the grid.
        if (skip && lane.view && skip(lane.view)) continue;
        try {
          this.pool.release(lane.view, "reel_spin_drum_release");
        } catch {
          // Already released / destroyed — safe to skip.
        }
      }
    }
    this.columns = [];
    this.resolveColumnOverlays();
  }

  /**
   * The landed (resting) symbol views for a column, by visible row (top→bottom).
   * Valid at `onColumnLanded(col)`. Empty when the column index is out of range.
   */
  public getLandedColumnViews(column: number): (SymbolView | null)[] {
    return this.columns[column]?.getLandedViewsInRowOrder() ?? [];
  }

  private pickSymbolId(): string {
    const ids = this.candidateSymbolIds;
    return ids[Math.floor(Math.random() * ids.length)];
  }
}
